import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@ucs/MobiusOverrides/TextField/TextField";
import { validateQuantityField } from "@ucs/Helpers/FormFieldsValidation";
import useAccessibleSubmit from "@ucs/Hooks/useAccessibleSubmit";
import React, { FC, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    /**
     * If true, the qtyOrdered will display in a text field for editing.
     * If false, the qtyOrdered will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onQtyOrderedChange?: (qtyOrdered: number) => void;
    extendedStyles?: CartLineQuantityStyles;
    label?: string;
    dynamicWidth?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    isLoading: state.data.carts.isLoading,
    cartLoadedTime: state.data.carts.cartLoadedTime,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasCartLineContext;

export interface CartLineQuantityStyles {
    editableQuantityTextField?: TextFieldProps;
    readOnlyQuantityText?: SmallHeadingAndTextStyles;
}

export const cartLineQuantityStyles: CartLineQuantityStyles = {
    editableQuantityTextField: {
        labelProps: {
            variant: "legend",
        },
    },
};

const CartLineQuantity: FC<Props> = ({
    cart,
    cartLine,
    editable = false,
    label,
    isLoading,
    cartLoadedTime,
    onQtyOrderedChange = () => {},
    extendedStyles,
    dynamicWidth,
}) => {
    const qtyKeyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    const localOnQtyOrderedChange = (value: string, onError?: () => void) => {
        const inputQty = parseFloat(value);
        if (Number.isNaN(inputQty)) {
            onError?.();
            return;
        }

        onQtyOrderedChange(inputQty);
    };

    const { value, changeHandler, keyDownHandler, blurHandler } = useAccessibleSubmit(
        cartLine.qtyOrdered!.toString(),
        localOnQtyOrderedChange,
        cartLoadedTime,
    );
    const [quantityError, setQuantityError] = useState("");
    const [styles] = React.useState(() => mergeToNew(cartLineQuantityStyles, extendedStyles));
    const [dynamicStyles, setDynamicStyles] = React.useState(() => mergeToNew(cartLineQuantityStyles, extendedStyles));
    const [currentWidth, setCurrentWidth] = React.useState("");

    const setInputStyles = () => {
        const val = value ? parseInt(value, 10) : 1;
        const width = val < 100 ? "60px" : val < 1000 ? "68px" : val < 10000 ? "76px" : "90px";
        if (width !== currentWidth) {
            setCurrentWidth(width);
            setDynamicStyles({
                editableQuantityTextField: {
                    labelProps: {
                        variant: "legend",
                        ...styles.editableQuantityTextField?.labelProps,
                    },
                    cssOverrides: {
                        ...styles.editableQuantityTextField?.cssOverrides,
                        inputSelect: css`
                            width: ${width};
                            ${styles.editableQuantityTextField?.cssOverrides?.inputSelect}
                        `,
                    },
                },
            });
        }
    };
    React.useEffect(() => {
        if (typeof value !== "undefined") {
            validateQuantity();
        }
    }, [value]);

    const validateQuantity = () => {
        const errorMessage = validateQuantityField(value);
        setQuantityError(errorMessage);
        return !errorMessage;
    };

    useEffect(() => {
        dynamicWidth && setInputStyles();
    }, [value]);

    if (editable) {
        return (
            <TextField
                {...dynamicStyles.editableQuantityTextField}
                type="number"
                label={label || translate("QTY")}
                error={quantityError}
                value={value}
                disabled={!cart.canModifyOrder || cartLine.isPromotionItem || cart.type === "Job" || isLoading[cart.id]}
                onKeyPress={qtyKeyPressHandler}
                onChange={changeHandler}
                onKeyDown={keyDownHandler}
                onBlur={blurHandler}
                onClick={(event: React.MouseEvent<HTMLInputElement>) => event.currentTarget.focus()}
                data-test-selector="cartline_qty"
            />
        );
    }

    return (
        <SmallHeadingAndText
            {...styles.readOnlyQuantityText}
            heading={label || translate("QTY")}
            text={cartLine.qtyOrdered!}
            data-test-selector="quoteDetail_cardLine_qty"
        />
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineQuantity));
